import { useCallback, useMemo, useState } from 'react';
import { useInvoiceParamsPresence, useMultipleForms, useSearchTable } from '../hooks';
import { getInputTableColumns, getReceiptVoucherColumns } from '../cols-definition';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { initialSearchValues, SearchFormValues } from '../schema';
import { PhieuThu, PhieuThuInput } from '@/types/schemas';
import type { PhieuThuFormInitialData } from '../types';
import { TABLE_PAGE_SIZE } from '../constants';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

export const usePhieuThuTable = () => {
  const hasInvoiceParams = useInvoiceParamsPresence();
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const crud = useCRUD<PhieuThu, PhieuThuInput>({
    endpoint: QUERY_KEYS.PHIEU_THU,
    pageSize: TABLE_PAGE_SIZE,
    initialFetch: !hasInvoiceParams,
    sumField: 't_tien',
    useFilter: true
  });

  const form = useForms<PhieuThu>(crud.fetchDetail);
  const searchTable = useSearchTable({
    searchData: crud.searchData,
    searchParams,
    setSearchParams,
    handleViewClick: form.handleViewClick,
    sumTotal: crud.sumTotal,
    isFiltered: crud.isFiltered,
    handlePageChange: crud.handlePageChange
  });
  const multipleForms = useMultipleForms({ addPhieuThu: crud.addItem, editPhieuThu: crud.updateItem });

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      setSearchParams(filters);
      await searchTable.handleInitialSearch(filters);
    },
    [searchTable]
  );

  const handleRefresh = useCallback(async () => {
    if (searchTable.hasFilterParams) {
      await searchTable.handleRefreshClick();
    } else if (crud.isFiltered) {
      await crud.searchData(searchParams);
    } else {
      await crud.refreshData();
    }

    if (form.selectedObj?.uuid) {
      await crud.fetchDetail(form.selectedObj.uuid);
    }
  }, [crud, form.selectedObj?.uuid, searchParams, searchTable]);

  const displayData = useMemo(
    () => (searchTable.hasFilterParams ? searchTable.searchState.searchResults : crud.data || []),
    [crud.data, searchTable.hasFilterParams, searchTable.searchState.searchResults]
  );
  const resolvedCurrentPage = searchTable.hasFilterParams ? searchTable.searchState.currentPage : crud.currentPage;
  const resolvedTotalItems = searchTable.hasFilterParams ? searchTable.searchState.totalCount : crud.totalItems;
  const resolvedSumTotal = searchTable.hasFilterParams
    ? (searchTable.searchState.totalRow?.t_tien ?? crud.sumTotal)
    : crud.sumTotal;
  const columns = useMemo(
    () => getReceiptVoucherColumns(form.handleViewClick, resolvedSumTotal) as DataTableColumn[],
    [form.handleViewClick, resolvedSumTotal]
  );
  const tables = useMemo(
    () => [
      {
        name: 'Tất cả',
        rows: displayData,
        columns
      }
    ],
    [columns, displayData]
  );

  return {
    crud,
    form,
    searchDialog: {
      open: form.showSearch,
      searchParams,
      onOpen: form.handleSearch,
      onClose: form.handleCloseSearch,
      onSubmit: handleSearchSubmit
    },
    multipleFormDialog: {
      open: multipleForms.showMultipleForms,
      initialData: multipleForms.initialData,
      invoiceData: multipleForms.invoiceData,
      currentFormIndex: multipleForms.currentFormIndex,
      formMode: multipleForms.formMode,
      onSubmit: multipleForms.handleMultipleFormSubmit,
      onClose: multipleForms.handleCloseMultipleForms
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading: crud.isLoading || crud.isSearching || searchTable.isFiltering,
      currentPage: resolvedCurrentPage,
      totalItems: resolvedTotalItems,
      onPageChange: searchTable.onPageChange,
      onSearchChange: searchTable.handleSearchChange,
      onRefresh: handleRefresh
    },
    detailTable: {
      rows: form.selectedObj ? (crud.detail as PhieuThuFormInitialData | null | undefined)?.child_data || [] : [],
      columns: getInputTableColumns()
    },
    selection: {
      selectedObj: form.selectedObj,
      selectedRowIndex: form.selectedRowIndex,
      clearSelection: form.clearSelection
    }
  };
};
